import React, { useState, useEffect, useCallback } from 'react';
import { Provider, defaultTheme, darkTheme, ProgressCircle, Divider, Checkbox } from '@adobe/react-spectrum';
import { SearchAutocomplete, Item } from '@react-spectrum/autocomplete';
import axios from 'axios';
import styles from '../styles/styles.module.css';
import themes from '../themes';
import localization from '../language/localization';
import getAuthHeaders from '../helpers/apiHelpers';
import Vip from '../themes/Icons/Vip';


function useDebounce<T>(value: T, delay: number): [T] {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return [debouncedValue];
}

interface SearchOnlyPanelProps {
  baseUrl: string;
  darkMode: boolean;
  language: string;
  contextData?: any;
  callBackEvent: (eventType: string, eventData: any) => void;
}

interface Employee {
  name: string;
  ldap: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  originalPhone?: string;
}

interface EmployeeInfo {
  'Full Name': string[];
  'Emp Status': string[];
  'Emp Type': string[];
  'Country': string[];
  'City': string[];
  'Email': string[];
  'Mgr Email': string[];
  'Office': string[];
  'Contact': string[];
  vip: string;
  sensitivity: string;
  isDirectorOrAbove: string;
}

const SearchOnlyPanel: React.FC<SearchOnlyPanelProps> = ({
  baseUrl,
  darkMode,
  language,
  contextData,
  callBackEvent
}) => {
  const [agentInput, setAgentInput] = useState('');
  const [searchResult, setSearchResult] = useState<Employee[] | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployee, setFetchingEmployee] = useState(false);
  const [error, setError] = useState('');
  const [isConversationActive] = useState(true);

  const [debouncedAgentInput] = useDebounce(agentInput, 300);

  const getConversationId = () => {
    const { ocPlatformData } = contextData || {};
    const { chatInfo } = ocPlatformData || {};
    return chatInfo?.contactId || '';
  };

  const texts = localization[language as keyof typeof localization] || localization.en;

  const searchEmployee = useCallback(async (input: string, autolink: boolean = true, updateFields: boolean = false, getSingleMatch: boolean = false) => {
    setError('');
    setLoading(true);

    try {
      const ldap = input.includes('@') ? input.split('@')[0] : input.trim();
      if (!ldap.trim() || ldap.includes('(')) {
        setSearchResult(null);
        return;
      }

      const conversationId = getConversationId();

      const response: any = await axios.get(`${baseUrl}/api/customer/v1/search`, {
        params: {
          input: ldap,
          conversationId,
          autolink,
          getSingleMatch
        },
        headers: getAuthHeaders(),
      });

      if (response.data?.success && response.data?.data?.customerDetails) {
        const customerDetails = response.data.data.customerDetails;
        setSearchResult(customerDetails);

        if (response.data?.data?.customerDetails?.length > 0) {
          const customerData = customerDetails[0];

          if (updateFields) {
            // Create employee info structure from API response
            const employeeInfo: EmployeeInfo = {
              'Full Name': [customerData.name || `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim()],
              'Emp Status': [customerData.status || 'N/A'],
              'Emp Type': [customerData.type || 'N/A'],
              'Country': [customerData.country || 'N/A'],
              'City': [customerData.city || 'N/A'],
              'Email': [customerData.email || 'N/A'],
              'Mgr Email': [customerData.managerEmail || 'N/A'],
              'Office': [customerData.office || 'N/A'],
              'Contact': [customerData.originalPhone || customerData.phone || 'N/A'],
              vip: customerData.vip || '0',
              sensitivity: customerData.sensitivity || '0',
              isDirectorOrAbove: customerData.isDirectorOrAbove || 'false'
            };

            setSelectedEmployee(employeeInfo);

            const eventData = {
              firstName: customerData.firstName || '',
              lastName: customerData.lastName || '',
              email: customerData.email || '',
              phone: customerData.originalPhone || customerData.phone || '',
              rengaId: customerData.ldap || '',
              username: customerData.ldap || '',
              contactId: conversationId || ''
            };
            callBackEvent('UPDATE_CUSTOMER_INFO', eventData);
            setFetchingEmployee(false);
          }
        }
      } else {
        setSearchResult([]);
        setError('No employee found');
      }
    } catch (err) {
      console.error('Error searching employee:', err);
      setError('Error searching employee');
      callBackEvent('SEARCH_ERROR', { error: err });
      setFetchingEmployee(false);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, callBackEvent, contextData]);

  // Auto-search when debounced input changes
  useEffect(() => {
    if (debouncedAgentInput && isConversationActive) {
      searchEmployee(debouncedAgentInput, false);
    }
  }, [debouncedAgentInput, isConversationActive, searchEmployee]);

  // Handle input change
  const handleInputChange = (value: string) => {
    setAgentInput(value);
    if (value.trim() === '') {
      setSearchResult(null);
      setSelectedEmployee(null);
    }
  };

  // Handle key press (Enter to search)
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const trimmedInput = agentInput.trim();
      setAgentInput(trimmedInput);
      searchEmployee(trimmedInput, false, false);
    }
  };

  // Handle employee selection from autocomplete
  const handleEmployeeSelect = (ldap: string) => {
    setFetchingEmployee(true);
    searchEmployee(ldap, false, true, true);
  };

  // Get theme classes
  const { darkModeBG, darkModeWidgetHeader, darkModea, darkModeTitle, darkModeFieldValues } = themes(darkMode);

  // Helper function to check if fields should be displayed
  const shouldDisplayFields = (email: string | string[]) => {
    if (Array.isArray(email)) {
      return email.length > 0;
    }
    return email !== '';
  };

  // Helper function to validate phone number
  const isValidPhoneNumber = (phone: string): boolean => {
    if (!phone || phone === 'N/A' || phone === 'null' || phone.trim() === '') {
      return false;
    }

    // Basic phone number validation - contains digits and common phone characters
    const phoneRegex = /^[\+]?[1-9][\d\-\s\(\)\.]{7,}$/;
    return phoneRegex.test(phone.trim());
  };

  return (
    <Provider theme={darkMode ? darkTheme : defaultTheme} colorScheme="light" UNSAFE_style={{ height: '100%', width: '100%', borderRadius: '5px' }}>
      <div className={styles['customer-info-panel'] + darkModeBG} style={{ height: '100%' }}>
        {/* Header - Employee Search */}
        <div className={styles['cip-widget-header'] + darkModeWidgetHeader}>
          <div className={styles['widget-icon']}>ES</div>
          <span>{texts['cip-title-header']}</span>
        </div>

        <Divider size="S" />

        {/* Employee Search - exactly like Accordion.tsx Employee Info */}
        <div style={{ padding: '16px' }}>
          <SearchAutocomplete
            width="100%"
            label="Search Employee (Name/LDAP/EmpID)"
            placeholder="Start typing to search employees..."
            items={searchResult || []}
            inputValue={agentInput}
            onInputChange={handleInputChange}
            loadingState={loading ? 'loading' : 'idle'}
            onKeyDown={handleKeyPress}
            onSubmit={(_key, value) => {
              const selectedEmployee: any = searchResult?.find((item: any) => item.ldap === value);
              if (selectedEmployee && selectedEmployee.ldap) {
                handleEmployeeSelect(selectedEmployee.ldap);
              } else {
                setError('Employee not found');
              }
            }}
            UNSAFE_style={{ marginBottom: '10px' }}
            isDisabled={!isConversationActive}
          >
            {(item: Employee) => <Item key={item.ldap}>{`${item.ldap} (${item.name})`}</Item>}
          </SearchAutocomplete>

          {/* Show fetching employee loader */}
          {fetchingEmployee && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <ProgressCircle aria-label="Loading..." isIndeterminate size="S" />
              <p style={{ marginTop: '10px', color: darkMode ? '#999' : '#666' }}>
                {texts['loader-fetching-customer-profile']}
              </p>
            </div>
          )}

          {/* Display selected employee info like original Employee Info */}
          {selectedEmployee && shouldDisplayFields(selectedEmployee['Email']) && !fetchingEmployee && (
            <div>
              {/* VIP/Sensitivity flags */}
              <div className={styles['flag-container']}>
                {selectedEmployee.sensitivity === '1' && <Checkbox isSelected={true}>Sensitivity</Checkbox>}
                {selectedEmployee.vip === '1' && <Vip isDark={darkMode} />}
              </div>

              {/* Employee fields */}
              {Object.entries(selectedEmployee).map(([key, value]) => (
                !['Title', 'vip', 'sensitivity', 'isDirectorOrAbove'].includes(key) && value && value[0] && (
                  <div key={key} className={styles['accordion-content-fields']}>
                    {key === 'Full Name' ? (
                      <div className={styles['full-name-label-container']}>
                        <span className={styles['accordion-content-fields-keys-cip'] + darkModeTitle}>
                          {texts[key] || key}:
                        </span>
                        {selectedEmployee.isDirectorOrAbove === 'true' && (
                          <span className={styles['director-star']} title="Director and Above">★</span>
                        )}
                      </div>
                    ) : (
                      <span className={styles['accordion-content-fields-keys-cip'] + darkModeTitle}>
                        {texts[key] || key}:
                      </span>
                    )}
                    {key === 'Contact' && isValidPhoneNumber(Array.isArray(value) ? value[0] : value) ? (
                      <button
                        onClick={() => callBackEvent('CIP_PHONE_NUMBER_CLICK', Array.isArray(value) ? value[0] : value)}
                        className={styles['accordion-content-fields-values-phone'] + darkModeBG + darkModea}
                      >
                        {Array.isArray(value) ? value[0] : value}
                      </button>
                    ) : (
                      <span className={styles['accordion-content-fields-values-isnota'] + darkModeBG + darkModeFieldValues}>
                        {Array.isArray(value) ? value[0] : value}
                      </span>
                    )}
                  </div>
                )
              ))}
            </div>
          )}

          {/* Error message */}
          {error && !loading && (
            <div style={{ color: '#d73a49', padding: '10px', textAlign: 'center' }}>
              {error}
            </div>
          )}


        </div>
      </div>
    </Provider>
  );
};

export default SearchOnlyPanel; 